import type { Activity, Contact, Opportunity, Proposal, Stage } from "../types";
import { STAGE_RANK } from "../types";
import { isPast } from "./format";

export const HIGH_VALUE_THRESHOLD = 50000;
export const FOLLOW_UP_DAYS = 7;
export const MAX_DISCOUNT_WITHOUT_APPROVAL = 20;
export const LOW_PROBABILITY_THRESHOLD = 30;

export interface MoveCheck {
  allowed: boolean;
  reason?: string;
  requiresConfirmation?: boolean;
  confirmMessage?: string;
}

function activeProposalFor(opportunityId: string, proposals: Proposal[]): Proposal | undefined {
  return proposals
    .filter((p) => p.opportunityId === opportunityId)
    .sort((a, b) => new Date(b.generatedDate).getTime() - new Date(a.generatedDate).getTime())[0];
}

/** Oportunitatile Won/Lost sunt stage-uri terminale - overdue/follow-up nu se mai aplica. */
export function isTerminalStage(stage: Stage): boolean {
  return stage === "Won" || stage === "Lost";
}

export function isOverdue(opp: Opportunity, now: Date): boolean {
  if (isTerminalStage(opp.stage)) return false;
  return isPast(opp.expectedCloseDate, now);
}

/**
 * Verifica daca o oportunitate poate fi mutata in stage-ul tinta.
 * NU trateaza Won (necesita date suplimentare, vezi canMarkWon) - drop-ul in
 * coloana Won din Kanban trebuie sa deschida modalul dedicat in loc sa apeleze asta.
 */
export function canMoveStage(opp: Opportunity, proposals: Proposal[], target: Stage, now: Date): MoveCheck {
  if (target === opp.stage) return { allowed: true };

  if (target !== "Lost" && STAGE_RANK[target] > STAGE_RANK[opp.stage] && isOverdue(opp, now)) {
    return {
      allowed: false,
      reason:
        "Opportunity cannot be moved to a later stage because the expected close date has passed. Update the close date first.",
    };
  }

  if (target === "Proposal Sent") {
    if (!opp.value) {
      return {
        allowed: false,
        reason: "Opportunity cannot be moved to Proposal Sent because it has no estimated value.",
      };
    }
    if (!opp.primaryContactId) {
      return {
        allowed: false,
        reason: "Opportunity cannot be moved to Proposal Sent because no primary contact has been selected.",
      };
    }
    if (!opp.expectedCloseDate) {
      return {
        allowed: false,
        reason: "Opportunity cannot be moved to Proposal Sent because it has no expected close date.",
      };
    }
  }

  if (target === "Negotiation") {
    const proposal = activeProposalFor(opp.id, proposals);
    if (!proposal || proposal.status === "Draft") {
      return {
        allowed: false,
        reason: "Opportunity cannot be moved to Negotiation because no proposal has been sent yet.",
      };
    }
    if (!proposal.amount) {
      return {
        allowed: false,
        reason: "Opportunity cannot be moved to Negotiation because the proposal has no amount.",
      };
    }
    if (isPast(proposal.expirationDate, now) || proposal.status === "Expired") {
      return {
        allowed: false,
        reason: "Opportunity cannot be moved to Negotiation because the proposal has expired.",
      };
    }
    if (opp.probability < LOW_PROBABILITY_THRESHOLD) {
      return {
        allowed: true,
        requiresConfirmation: true,
        confirmMessage: `This opportunity has a low probability of closing (${opp.probability}%). Move it to Negotiation anyway?`,
      };
    }
  }

  return { allowed: true };
}

export interface WonCheck {
  allowed: boolean;
  reasons: string[];
}

export function canMarkWon(opp: Opportunity, finalValue: number | undefined, productId: string | undefined, contractStartDate: string | undefined): WonCheck {
  const reasons: string[] = [];
  if (!finalValue) reasons.push("Final value must be filled in.");
  if (!productId) reasons.push("A plan or service must be selected.");
  if (!contractStartDate) reasons.push("The contract start date must be confirmed.");
  return { allowed: reasons.length === 0, reasons };
}

export function canAcceptProposal(proposal: Proposal, now: Date): { allowed: boolean; reason?: string } {
  if (proposal.status === "Expired" || isPast(proposal.expirationDate, now)) {
    return { allowed: false, reason: "An expired proposal cannot be accepted." };
  }
  return { allowed: true };
}

export function needsDiscountApproval(proposal: Proposal): boolean {
  return proposal.discountPercent > MAX_DISCOUNT_WITHOUT_APPROVAL && !proposal.approved;
}

export type WarningCode =
  | "overdue"
  | "needs-follow-up"
  | "no-primary-contact"
  | "no-decision-maker"
  | "high-value"
  | "expired-proposal"
  | "low-probability";

export interface Warning {
  code: WarningCode;
  label: string;
  severity: "warning" | "danger" | "info";
}

export function computeWarnings(
  opp: Opportunity,
  activities: Activity[],
  proposals: Proposal[],
  contacts: Contact[],
  now: Date
): Warning[] {
  const warnings: Warning[] = [];
  const terminal = isTerminalStage(opp.stage);

  if (!terminal && isOverdue(opp, now)) {
    warnings.push({ code: "overdue", label: "Overdue", severity: "danger" });
  }

  if (!terminal) {
    const oppActivities = activities.filter((a) => a.opportunityId === opp.id);
    const lastTimestamp = oppActivities.reduce<number>((max, a) => Math.max(max, new Date(a.timestamp).getTime()), new Date(opp.createdAt).getTime());
    const days = Math.floor((now.getTime() - lastTimestamp) / (1000 * 60 * 60 * 24));
    if (days > FOLLOW_UP_DAYS) {
      warnings.push({ code: "needs-follow-up", label: `No activity in ${days} days`, severity: "warning" });
    }
  }

  if (!terminal && !opp.primaryContactId) {
    warnings.push({ code: "no-primary-contact", label: "No primary contact", severity: "warning" });
  }

  if (!terminal && opp.primaryContactId) {
    const contact = contacts.find((c) => c.id === opp.primaryContactId);
    if (contact && !contact.decisionMaker) {
      warnings.push({ code: "no-decision-maker", label: "No decision maker identified", severity: "warning" });
    }
  }

  if ((opp.value ?? 0) > HIGH_VALUE_THRESHOLD) {
    warnings.push({ code: "high-value", label: "High Value", severity: "info" });
  }

  if (!terminal) {
    const proposal = activeProposalFor(opp.id, proposals);
    if (proposal && (proposal.status === "Expired" || isPast(proposal.expirationDate, now))) {
      warnings.push({ code: "expired-proposal", label: "Proposal expired", severity: "danger" });
    }
  }

  if (opp.stage === "Negotiation" && opp.probability < LOW_PROBABILITY_THRESHOLD) {
    warnings.push({ code: "low-probability", label: "Low probability", severity: "warning" });
  }

  return warnings;
}

export function lastActivityDate(opportunityId: string, activities: Activity[], createdAt: string): string {
  const oppActivities = activities.filter((a) => a.opportunityId === opportunityId);
  if (oppActivities.length === 0) return createdAt;
  return oppActivities.reduce((latest, a) => (new Date(a.timestamp) > new Date(latest) ? a.timestamp : latest), createdAt);
}
